/**
 * Lightweight session store for token-based (non-SRA) administration sessions:
 * /secure, /remote, and eventually /link. This deliberately does NOT reuse
 * `createUserStore`/`userStore.ts` — that store is built for SRA/family-account
 * semantics (family members, profile permissions, device preferences, boot
 * sequence replay) that don't apply to a single respondent taking one
 * assessment. This store implements the same `UserState` interface (so it can
 * be dropped into `AdminView`/`ReportView`'s `useUserStore()` unmodified — see
 * `contexts/StoreContext.tsx`'s `RespondentStoreProvider`) but every method
 * outside the small set AdminView/resolveAuthContext actually touch
 * (`session`, `checkStoredAuth`, `restoreSessionFromStorage`, `getStoredItem`,
 * `setApiService`/`getApiService`, `isSessionValid`) is a safe, honest no-op —
 * see ApiService.ts's `resolveAuthContext`/`doRefreshStoredAuthentication` for
 * the exact minimal contract this was derived from.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import {
  UserState,
  AppUser,
  FamilyAccount,
  createDefaultUserSession,
  createDefaultUserPreferences,
  createDefaultAppState,
} from '../types/user';
import type { StorageInterface } from './childNavStores';
import type { SecureLoginResponseDto } from '../types/api/tokenAssessment';
import { getPhiValue } from '../types/api/patinfo';
import type { ApiService } from '../services/api';

/**
 * Wraps a StorageInterface so every key is transparently prefixed before
 * hitting the real backing store. ApiService's refresh machinery
 * (`getAuthTimestampAgeMinutes`, `doRefreshStoredAuthentication`,
 * `processRefreshResponse`) reads/writes hardcoded literal keys
 * (`auth_token`, `refresh_token`, `auth_timestamp`, `userSession`) that are
 * NOT configurable — so isolating a respondent session's tokens from an SRA
 * session that might be logged into the same browser/device requires
 * namespacing the underlying storage, not renaming the keys ApiService itself
 * uses. Only the storage handle passed as the respondent session's
 * `authContext.storage` should be wrapped this way — content storage
 * (bundled AdminSegment/AdminAssessment data) is fine to share unprefixed.
 */
export function createNamespacedStorage(storage: StorageInterface, namespace: string): StorageInterface {
  const prefix = `${namespace}:`;
  return {
    getItem: (key: string) => storage.getItem(`${prefix}${key}`),
    setItem: (key: string, value: string) => storage.setItem(`${prefix}${key}`, value),
    removeItem: (key: string) => storage.removeItem(`${prefix}${key}`),
  };
}

const RESPONDENT_ROLE: AppUser['role'] = 'provider';

/**
 * AdminView's restoreSessionUser() throws if userId AND email are both blank
 * (it's SRA-oriented: no email means no fallback identity for it to use). A
 * respondent session never has an email, so userId must never be blank —
 * fall back to a deterministic id from accountId/patcode/assessmentId if the
 * backend ever returns an empty sessionId, and flag it since sessionId being
 * blank is itself unexpected (see SecureLoginResponseDto.sessionId's contract).
 */
function resolveRespondentUserId(resolved: SecureLoginResponseDto): string {
  if (resolved.sessionId) {
    return resolved.sessionId;
  }
  console.warn('respondentSessionStore: SecureLoginResponseDto.sessionId was blank — falling back to a derived id');
  const patcode = getPhiValue(resolved.phi ?? [], 'patcode');
  return `secure:${resolved.accountId}:${patcode}:${resolved.assessmentId}`;
}

function buildRespondentUser(resolved: SecureLoginResponseDto): AppUser {
  return {
    userId: resolveRespondentUserId(resolved),
    familyId: resolved.accountId,
    name: resolved.dName || 'Respondent',
    email: '',
    role: RESPONDENT_ROLE,
    accessType: resolved.accessType || undefined,
    authToken: resolved.token,
    refreshToken: resolved.refreshToken,
    lastLoginDate: new Date().toISOString(),
    preferences: createDefaultUserPreferences(),
    appState: createDefaultAppState(),
    profilePermissions: {},
    createdDate: new Date().toISOString(),
    lastActiveDate: new Date().toISOString(),
    deviceInfo: {
      platform: (typeof globalThis !== 'undefined' && 'window' in globalThis) ? 'web' as const : 'mobile' as const,
      appVersion: '1.0.0',
    },
  };
}

function buildRespondentFamilyAccount(resolved: SecureLoginResponseDto): FamilyAccount {
  return {
    familyId: resolved.accountId,
    familyName: 'Secure Administration',
    accountTier: 'standard',
    subscriptionStatus: 'active',
    createdDate: new Date().toISOString(),
    lastActiveDate: new Date().toISOString(),
    settings: {
      dataRetentionDays: 365,
      allowDataSharing: false,
      backupEnabled: false,
      syncEnabled: true,
    },
  };
}

const SESSION_STORAGE_KEY = 'userSession';
const AUTH_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const AUTH_TIMESTAMP_KEY = 'auth_timestamp';

/**
 * Creates the respondent session store from a resolved token-assessment
 * response. `storage` should already be namespaced (see
 * `createNamespacedStorage`) so the literal keys this writes never collide
 * with an SRA session's own storage on the same device.
 */
export const createRespondentSessionStore = (
  resolved: SecureLoginResponseDto,
  storage: StorageInterface
) => {
  let apiServiceRef: ApiService | null = null;

  const store = create<UserState>()(
    subscribeWithSelector((set, get) => ({
      session: {
        ...createDefaultUserSession(),
        user: buildRespondentUser(resolved),
        familyAccount: buildRespondentFamilyAccount(resolved),
        isAuthenticated: true,
      },
      familyUsers: [],

      // ── Not applicable to a respondent session — no password/passkey/OTP login path exists here. ──
      login: async () => {
        console.warn('respondentSessionStore: login() is not supported for a token-based administration session');
        return false;
      },
      applyAuthSession: async () => {
        throw new Error('respondentSessionStore: applyAuthSession() is not supported for a token-based administration session');
      },

      logout: async () => {
        await storage.removeItem(SESSION_STORAGE_KEY);
        await storage.removeItem(AUTH_TOKEN_KEY);
        await storage.removeItem(REFRESH_TOKEN_KEY);
        await storage.removeItem(AUTH_TIMESTAMP_KEY);
        set((state) => ({
          ...state,
          session: { ...createDefaultUserSession(), isAuthenticated: false },
        }));
      },

      // Local-only rehydrate from this session's own namespaced storage — no
      // network call, matching the real store's contract exactly (see
      // ApiService.doRefreshStoredAuthentication's fallback path).
      restoreSessionFromStorage: async () => {
        try {
          const raw = await storage.getItem(SESSION_STORAGE_KEY);
          if (!raw) {
            return false;
          }
          const parsed = JSON.parse(raw) as { user?: AppUser; familyAccount?: FamilyAccount };
          if (!parsed.user) {
            return false;
          }
          set((state) => ({
            ...state,
            session: {
              ...state.session,
              user: parsed.user!,
              familyAccount: parsed.familyAccount ?? state.session.familyAccount,
              isAuthenticated: true,
            },
          }));
          return true;
        } catch (error) {
          console.warn('respondentSessionStore: restoreSessionFromStorage failed', error);
          return false;
        }
      },

      checkStoredAuth: async () => {
        return get().restoreSessionFromStorage();
      },

      getStoredItem: async (key: string) => storage.getItem(key),

      // ── Everything below is unused by AdminView/ReportView/resolveAuthContext
      // for a respondent session; kept as honest, safe defaults rather than
      // throwing, since a stray call here shouldn't crash an in-progress
      // administration. ──
      syncProfilesFromResponse: async () => ({ success: true, message: 'Not applicable to a respondent session' }),
      syncAdminHistoryFromResponse: async () => ({ success: true, message: 'Not applicable to a respondent session' }),
      syncBackendFromResponse: async () => ({
        success: true,
        message: 'Not applicable to a respondent session',
        results: {},
        errors: [],
      }),

      updateUser: async () => {},
      updatePreferences: async () => {},
      updateAppState: async () => {},

      inviteUser: async () => {
        throw new Error('respondentSessionStore: inviteUser() is not supported');
      },
      removeUser: async () => {},
      updateUserRole: async () => {},

      updateProfilePermissions: async () => {},
      canAccessProfile: () => true,
      canEditProfile: () => false,

      extendSession: () => {},
      isSessionValid: () => get().session.isAuthenticated,
      checkSessionTimeout: () => false,

      getCurrentUser: () => get().session.user,
      getFamilyAccount: () => get().session.familyAccount,
      getSelectedProfileId: () => getPhiValue(resolved.phi ?? [], 'patcode'),
      isPremiumUser: () => false,
      canInviteUsers: () => false,

      setApiService: (apiService) => {
        apiServiceRef = apiService;
      },
      getApiService: () => apiServiceRef,

      reset: () => {
        set((state) => ({
          ...state,
          session: { ...createDefaultUserSession(), isAuthenticated: false },
        }));
      },
    }))
  );

  // Persist the initial session immediately so a same-tab reload (before any
  // refresh has occurred) can still restoreSessionFromStorage() successfully,
  // and so ApiService's proactive-refresh age check has a real auth_timestamp
  // to read from the very first request.
  void storage.setItem(AUTH_TOKEN_KEY, resolved.token);
  void storage.setItem(REFRESH_TOKEN_KEY, resolved.refreshToken);
  void storage.setItem(AUTH_TIMESTAMP_KEY, new Date().toISOString());
  void storage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
    user: store.getState().session.user,
    familyAccount: store.getState().session.familyAccount,
    isAuthenticated: true,
  }));

  return store;
};
